/* eslint-disable react/no-deprecated */
/* eslint-disable import/no-cycle */
import React, { useEffect, useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import InputAdornment from '@material-ui/core/InputAdornment';
import TablePagination from '@material-ui/core/TablePagination';
import _ from 'lodash';
import GetAppIcon from '@material-ui/icons/GetApp';
import { PDFDownloadLink } from '@react-pdf/renderer';
import moment from 'moment';
import MuiCheckbox from '@material-ui/core/Checkbox';
import Toolbar from '@material-ui/core/Toolbar';
import { displayPriceFormat, isStringNotNull } from '../../utils/tools';
import Paper from '../Paper';
import Input from '../Input';
import IconButton from '../IconButton';
import { mainColor, UNIT } from '../../constants/theme';
import { calculateAge, getDateFormat, getTimeFromNumber } from '../../utils/suitableDate';
import { styles } from './DriverModal.styles';
import { handleClick, handleSelectAllClick, generateZip, initDriverData, getDataValue } from './DriverModal.service';
import { InvoiceTemplate } from '..';
import { INVOICE_TABLE_HEAD } from '../../constants/table';
import Spinner from '../Spinner';
import { NETWORK } from '../../constants';
import { useAppSelector } from 'src/hooks';
import { ExtendedDriverData } from 'src/models/drivers/driver';
import { BillingInputs, DriverBilling } from 'src/models/drivers/driverBilling';

const Checkbox = withStyles(() => ({
    checked: {
        color: `${mainColor.secondaryColor} !important`
    }
}))(MuiCheckbox);

export type DriverModalState = {
    page: number,
    rowsPerPage: number,
    selected: string[],
    ready: boolean,
    isLoading: boolean
}

export type DriverModalProps = {
    classes: {
        [key: string]: string;
    },
    driver: ExtendedDriverData;
    isIndependentTab: boolean,
    changeCollection: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

function DriverModal({ classes, driver, isIndependentTab, changeCollection }: DriverModalProps) {
    const { networkLegal, legal, shopId } = useAppSelector(state => state.user);
    const [billings, setBillings] = useState<DriverBilling[]>([]);
    const [driverState, setDriverState] = useState<ExtendedDriverData | null>(null)
    const [componentState, setComponentState] = useState<DriverModalState>({
        page: 0,
        rowsPerPage: 5,
        selected: [],
        ready: false,
        isLoading: false
    })

    useEffect(() => {
        (async () => {
            await initDriverData(driver, shopId, isIndependentTab, setDriverState, setBillings, setComponentState)
        }
        )()
        const timer = setTimeout(() => {
            setComponentState(currentState => ({
                ...currentState,
                ready: true
            }));
        }, 1);
        return () => clearTimeout(timer);
    }, [driver, shopId, isIndependentTab])

    const renderTableToolbar = () => {
        const numSelected = componentState.selected.length;
        return (
            <Toolbar className='d-flex py-0 px-0 ' style={{ minHeight: 0 }}>
                {numSelected > 0 ? (
                    <div className='px-3 d-flex w-100 justify-content-between align-items-center'>
                        <div className={classes.subtitle} style={{ color: mainColor.secondaryColor }}>
                            {numSelected} sélectionnée(s)
                        </div>
                        <IconButton
                            title='Télécharger les factures'
                            className={classes.iconStyle}
                            style={{ color: mainColor.secondaryColor }}
                            onClick={() => {
                                generateZip(componentState.selected, driver, billings, renderDriverInvoice);
                            }}
                        >
                            <GetAppIcon style={{ fontSize: UNIT }} />
                        </IconButton>
                    </div>
                ) : (
                    <div className={classes.subtitle}>FACTURES</div>
                )}
            </Toolbar>
        );
    };

    const displayOrderDetail = ({ title, body, color = '' }) => {
        return (
            <div className='mb-2'>
                <p className={classes.orderDetail}>
                    {title}
                    <b className={classes.textStyle} style={{ color }}>
                        {body}
                    </b>
                </p>
            </div>
        );
    };

    const createTableHead = (billings: DriverBilling[]) => {
        const numSelected = componentState.selected.length;
        const rowCount = _.size(billings);
        return (
            <TableHead>
                <TableRow style={{ height: 30 }}>
                    <TableCell padding='checkbox'>
                        <Checkbox
                            indeterminate={numSelected > 0 && numSelected < rowCount}
                            checked={rowCount > 0 && numSelected === rowCount}
                            onChange={e => handleSelectAllClick(e, billings, setComponentState)}
                            inputProps={{ 'aria-label': 'select all' }}
                        />
                    </TableCell>
                    {INVOICE_TABLE_HEAD.map((header, index) => {
                        return (
                            <TableCell key={index} align='center' className={classes.heading}>
                                {header}
                            </TableCell>
                        );
                    })}
                    <TableCell align='center' className={classes.heading} />
                </TableRow>
            </TableHead>
        );
    };

    const createTableRow = (billings: DriverBilling[]) => {
        const currentBilling = billings.slice(
            componentState.page * componentState.rowsPerPage,
            componentState.page * componentState.rowsPerPage + componentState.rowsPerPage
        );
        const isSelected = (chargeId: string) => componentState.selected.indexOf(chargeId) !== -1;
        return (
            <TableBody>
                {_.map(currentBilling, (billing: DriverBilling, index: number) => {
                    const isItemSelected = isSelected(billing.chargeId);
                    return (
                        <TableRow
                            key={index.toString()}
                            aria-checked={isItemSelected}
                            tabIndex={-1}
                            classes={{ selected: classes.selected }}
                            className={classes.tableRow}
                            selected={isItemSelected}
                        >
                            <TableCell padding='checkbox' className={classes.checkboxRow}>
                                <Checkbox checked={isItemSelected} onChange={() => handleClick(billing.chargeId, componentState.selected, setComponentState)} />
                            </TableCell>
                            <TableCell align='center' className={classes.orderRow}>
                                {billing.orderNumber}
                            </TableCell>
                            <TableCell align='center' className={classes.orderRow}>
                                {getDateFormat(billing.dateOrder, 'L')}
                            </TableCell>
                            <TableCell align='center' className={classes.orderRow}>
                                {displayPriceFormat(billing.delivery.fee)}
                            </TableCell>

                            <TableCell align='center' className={classes.checkboxRow}>
                                {componentState.ready && currentBilling && (
                                    <PDFDownloadLink
                                        document={renderDriverInvoice(billing)}
                                        fileName={`${getDateFormat(billing.dateOrder, 'DDMMYYYY')}${billing.orderNumber}`}
                                    >
                                        {({ loading }) =>
                                            loading ? (
                                                'Chargement du document...'
                                            ) : (
                                                <IconButton title='Télécharger la facture' className={classes.iconStyle}>
                                                    <GetAppIcon style={{ fontSize: UNIT }} />
                                                </IconButton>
                                            )
                                        }
                                    </PDFDownloadLink>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        );
    };

    const renderDriverInvoice = (selectedBilling: DriverBilling) => {
        const email = driverState?.email;
        const freelance = driverState?.freelance;
        const company = freelance?.company;
        const isNetworkChecked = _.find(selectedBilling.inputs, (input: BillingInputs) => input?.network?.debtor === NETWORK);
        return (
            <InvoiceTemplate
                isDriverInvoice
                isNetworkChecked={isNetworkChecked}
                creditorLegal={{
                    ...company,
                    companyName: `${driverState?.lastName} ${driverState?.firstName}`,
                    email
                }}
                legal={isNetworkChecked ? networkLegal : legal}
                billings={selectedBilling}
                dateInfo={selectedBilling.dateOrder}
                shopId={shopId}
                year={moment(selectedBilling.dateOrder).year()}
            />
        );
    };

    return (
        componentState.isLoading ?
            <>
                <div className='col'>
                    {displayOrderDetail({
                        title: 'ÉTAT : ',
                        body: driverState?.onTransaction
                    })}
                    {displayOrderDetail({
                        title: 'Nombre de livraisons : ',
                        body: driverState?.nbOrderSuccess
                    })}
                    {isIndependentTab &&
                        displayOrderDetail({
                            title: 'Coût total : ',
                            color: mainColor.colorRed,
                            body: displayPriceFormat(driverState?.fee || 0)
                        })}
                </div>
                <div className='col-sm p-2'>
                    <Paper className={classes.paperStyle}>
                        <div>
                            <p className={classes.subtitle}>INFORMATIONS LIVREUR</p>
                            <hr className={classes.hrStyle} />
                            <div className={classes.typoDetails}>
                                {' '}
                                <p className={classes.labelStyle}>Prénom :</p>
                                {getDataValue(driverState?.firstName ?? '')}
                            </div>
                            {isStringNotNull(driver.lastName) && (
                                <div className={classes.typoDetails}>
                                    <p className={classes.labelStyle}>Nom :</p>
                                    {getDataValue(driverState?.lastName ?? '')}
                                </div>
                            )}
                            {isStringNotNull(driver.birthDate) && (
                                <div className={classes.typoDetails}>
                                    <p className={classes.labelStyle}>Age :</p>{' '}
                                    {`${calculateAge(driverState?.birthDate)} ans (date de naissance ${driver?.birthDate}))`}
                                </div>
                            )}
                            <div className={classes.typoDetails}>
                                <p className={classes.labelStyle}>Email :</p>
                                {getDataValue(driverState?.email ?? '')}
                            </div>
                            <div className={classes.typoDetails}>
                                <p className={classes.labelStyle}>Numéro :</p>
                                {getDataValue(driverState?.numero ?? '')}
                            </div>
                            <div className={classes.typoDetails}>
                                <p className={classes.labelStyle}>Vitesse :</p>
                                {getDataValue(String(driverState?.speed))} <b> km/h</b>
                            </div>
                            <div className={classes.typoDetails}>
                                <p className={classes.labelStyle}>Durée moyenne :</p>
                                {getTimeFromNumber(parseFloat(getDataValue(String(driverState?.durationPerOrder))))} <b> min/course</b>
                            </div>
                            <div className={classes.typoDetails}>
                                <p className={classes.labelStyle}>Distance moyenne :</p>
                                {getDataValue(String(driverState?.distancePerOrder))} <b> km/course</b>
                            </div>
                            <div >
                                {!isIndependentTab && (
                                    <>
                                        <p>Valeurs récupérées :</p>
                                        <div className={classes.inputStyle}>
                                            <Input
                                                autoFocus
                                                margin='dense'
                                                id='cash'
                                                label='Espèces'
                                                type='number'
                                                classes={{
                                                    label: classes.label,
                                                    root: classes.root,
                                                    cssFocused: classes.cssFocused,
                                                    notchedOutline: classes.notchedOutline
                                                }}
                                                value={driver?.cash?.toString() ?? '0'}
                                                onChange={e => changeCollection(e)}
                                                fullWidth
                                                InputProps={{
                                                    endAdornment: <InputAdornment position='end'>€</InputAdornment>
                                                }}
                                            />
                                            <Input
                                                autoFocus
                                                margin='dense'
                                                id='ticket_resto'
                                                label='Tickets restaurant'
                                                type='number'
                                                classes={{
                                                    label: classes.label,
                                                    root: classes.root,
                                                    cssFocused: classes.cssFocused,
                                                    notchedOutline: classes.notchedOutline
                                                }}
                                                value={driver?.ticket_resto?.toString() ?? '0'}
                                                onChange={e => changeCollection(e)}
                                                fullWidth
                                                InputProps={{
                                                    endAdornment: <InputAdornment position='end'>€</InputAdornment>
                                                }}
                                            />
                                            <Input
                                                autoFocus
                                                margin='dense'
                                                id='cb'
                                                label='TPE'
                                                type='number'
                                                classes={{
                                                    label: classes.label,
                                                    root: classes.root,
                                                    cssFocused: classes.cssFocused,
                                                    notchedOutline: classes.notchedOutline
                                                }}
                                                value={driver?.cb?.toString() ?? '0'}
                                                onChange={e => changeCollection(e)}
                                                fullWidth
                                                InputProps={{
                                                    endAdornment: <InputAdornment position='end'>€</InputAdornment>
                                                }}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </Paper>
                </div>
                {isIndependentTab && (
                    <div className='col-sm p-2'>
                        <Paper className={classes.paperStyle}>
                            <div>
                                {renderTableToolbar()}
                                <hr className={classes.hrStyle} />
                                {driverState?.nbOrderSuccess && driverState?.nbOrderSuccess > 0 ? (
                                    <>
                                        {!billings && (
                                            <div className='text-center mt-4'>
                                                <Spinner size={20} />
                                            </div>
                                        )}
                                        {billings && (
                                            <Paper elevation={0} scrollOption='auto'>
                                                <Table className={classes.table}>
                                                    {createTableHead(billings)}
                                                    {createTableRow(billings)}
                                                </Table>

                                                <TablePagination
                                                    rowsPerPageOptions={[]}
                                                    component='div'
                                                    count={driverState?.nbOrderSuccess}
                                                    rowsPerPage={componentState.rowsPerPage}
                                                    page={componentState.page}
                                                    onPageChange={(event, page) => setComponentState(prevState => ({ ...prevState, page }))}
                                                />
                                            </Paper>
                                        )}
                                    </>
                                ) : (
                                    <p className={classes.detail} style={{ textAlign: 'center' }}>
                                        {`${driverState?.firstName} ne possède aucune facture. `}
                                    </p>
                                )}
                            </div>
                        </Paper>
                    </div>
                )}
            </> : <div className='text-center d-flex justify-content-center'>
                <Spinner size={20} />
            </div>
    );
}
export default withStyles(styles)(DriverModal)